import React, {
	createContext,
	forwardRef,
	useContext,
	useId,
	useMemo,
	type ComponentProps,
	type PropsWithChildren,
	type ReactNode,
} from 'react'

import type * as LabelPrimitives from '@radix-ui/react-label'
import { Slot } from '@radix-ui/react-slot'
import {
	Controller,
	FormProvider,
	useController,
	useFormContext,
	useFormState,
	type ControllerProps,
	type FieldPath,
	type FieldValues,
} from 'react-hook-form'

import { InformationCircleSolid } from '@/components/icons'
import { Hint as HintComponent } from '@/components/ui/hint'
import { Label as LabelComponent } from '@/components/ui/label'
import { Text } from '@/components/ui/text'
import { Tooltip } from '@/components/ui/tooltip'

import { cx } from '@/libs/cva'

const Provider = FormProvider

type FormFieldContextValue<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
	name: TName
}

const FormFieldContext = createContext<FormFieldContextValue>(
	{} as FormFieldContextValue
)

const Field = <
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
	...props
}: ControllerProps<TFieldValues, TName>) => {
	return (
		<FormFieldContext.Provider value={{ name: props.name }}>
			<Controller {...props} />
		</FormFieldContext.Provider>
	)
}

type FormItemContextValue = {
	id: string
}

const FormItemContext = createContext<FormItemContextValue>(
	{} as FormItemContextValue
)

const useFormField = () => {
	const fieldContext = useContext(FormFieldContext)
	const itemContext = useContext(FormItemContext)
	const { getFieldState, setFocus, trigger, control } = useFormContext()

	const formState = useFormState({ name: fieldContext.name })
	const fieldState = getFieldState(fieldContext.name, formState)

	const formControl = useController({ control, name: fieldContext.name })

	if (!fieldContext) {
		throw new Error('useFormField should be used within a FormField')
	}

	const { id } = itemContext

	return {
		id,
		name: fieldContext.name,
		formItemId: `${id}-form-item`,
		formLabelId: `${id}-form-item-label`,
		formDescriptionId: `${id}-form-item-description`,
		formErrorMessageId: `${id}-form-item-message`,
		...fieldState,
		onBlur: formControl.field.onBlur,
		onFocus: () => setFocus(fieldContext.name),
		trigger: () => trigger(fieldContext.name),
	}
}

const Item = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
	({ className, ...props }, ref) => {
		const id = useId()

		return (
			<FormItemContext.Provider value={{ id }}>
				<div
					ref={ref}
					className={cx('flex flex-col gap-1.5', className)}
					{...props}
				/>
			</FormItemContext.Provider>
		)
	}
)
Item.displayName = 'Form.Item'

const Label = forwardRef<
	React.ElementRef<typeof LabelPrimitives.Root>,
	React.ComponentPropsWithoutRef<typeof LabelPrimitives.Root> & {
		optional?: boolean
		tooltip?: ReactNode
		icon?: ReactNode
		required?: boolean
	}
>(({ className, optional = false, tooltip, icon, required, ...props }, ref) => {
	const { formLabelId, formItemId } = useFormField()

	return (
		<div className="flex items-center gap-x-1">
			<LabelComponent
				id={formLabelId}
				ref={ref}
				className={cx(
					{
						'relative after:pointer-events-none after:absolute after:ml-1 after:text-xs after:font-semibold after:text-ui-fg-error after:content-["*"]':
							required,
					},
					className
				)}
				htmlFor={formItemId}
				size="small"
				weight="plus"
				{...props}
			/>
			{tooltip && (
				<Tooltip content={tooltip}>
					<InformationCircleSolid className="text-ui-fg-muted" />
				</Tooltip>
			)}
			{icon}
			{optional && (
				<Text size="small" leading="compact" className="text-ui-fg-muted">
					(Opsional)
				</Text>
			)}
		</div>
	)
})
Label.displayName = 'Form.Label'

const Control = forwardRef<
	React.ElementRef<typeof Slot>,
	React.ComponentPropsWithoutRef<typeof Slot>
>(({ ...props }, ref) => {
	const {
		error,
		formItemId,
		formDescriptionId,
		formErrorMessageId,
		formLabelId,
	} = useFormField()

	return (
		<Slot
			ref={ref}
			id={formItemId}
			aria-describedby={
				!error
					? `${formDescriptionId}`
					: `${formDescriptionId} ${formErrorMessageId}`
			}
			aria-invalid={!!error}
			aria-labelledby={formLabelId}
			{...props}
		/>
	)
})
Control.displayName = 'Form.Control'

const Hint = forwardRef<
	HTMLParagraphElement,
	React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => {
	const { formDescriptionId } = useFormField()

	return (
		<HintComponent
			ref={ref}
			id={formDescriptionId}
			className={className}
			{...props}
		/>
	)
})
Hint.displayName = 'Form.Hint'

const ErrorMessage = forwardRef<
	HTMLParagraphElement,
	React.HTMLAttributes<HTMLParagraphElement>
>(({ className, children, ...props }, ref) => {
	const { error, formErrorMessageId } = useFormField()
	const msg = error ? String(error?.message) : children

	if (!msg || msg === 'undefined') {
		return null
	}

	return (
		<HintComponent
			ref={ref}
			id={formErrorMessageId}
			className={className}
			variant={error ? 'error' : 'info'}
			{...props}
		>
			{msg}
		</HintComponent>
	)
})
ErrorMessage.displayName = 'Form.ErrorMessage'

/**
 * Form Extended
 * this is a wrapper for the form provider integrated with react query and react-hook-form
 */

type IExtendedFormCtx = {
	isPending?: boolean
	mode?: 'create' | 'update'
	onCancel?: () => void
	onSubmit?: ComponentProps<'form'>['onSubmit']
}

const ExtendedFormCtx = createContext<IExtendedFormCtx | null>(null)

function ExtendedProvider({
	isPending = false,
	mode = 'create',
	onCancel,
	onSubmit,
	children,
}: PropsWithChildren<IExtendedFormCtx>) {
	const value = useMemo(
		() => ({ isPending, mode, onCancel, onSubmit }),
		[isPending, mode, onCancel, onSubmit]
	)

	return (
		<ExtendedFormCtx.Provider value={value}>
			{children}
		</ExtendedFormCtx.Provider>
	)
}

function useFormExtended() {
	const context = useContext(ExtendedFormCtx)
	if (!context) {
		throw new Error(
			'useFormExtended must be used within a Form.ExtendedProvider'
		)
	}
	return context
}

const Form = Object.assign(Provider, {
	Item,
	Label,
	Control,
	Hint,
	ErrorMessage,
	Field,
	ExtendedProvider,
})

export { Form, useFormExtended }
